import { FC, useState } from 'react';
import { TextField, Typography, Divider, InputAdornment } from '@mui/material';
import { Control, Controller } from 'react-hook-form';
import FormField from 'further-ui/components/FormField';
import { Column, Row } from 'further-ui/components/Layout';
import { FormValues } from '../../types';
import LogoUploadBox from './LogoUploadBox';
import ColourPicker from './ColorPicker';
import CkEditor from 'components/CkEditor';
import Toggle from 'components/new/Toggle';

type Props = {
  control: Control<FormValues>;
  hasLinkedPortal: boolean;
  sharedFieldWarningText: string;
  sharedFieldDotColor: string;
};

enum LegalFooterTextTab {
  Portal = 'portal',
  Email = 'email',
}

const PortalBranding: FC<Props> = ({
  control,
  hasLinkedPortal,
  sharedFieldWarningText,
  sharedFieldDotColor,
}) => {
  const [legalFooterTextTab, setLegalFooterTextTab] =
    useState<LegalFooterTextTab>(LegalFooterTextTab.Portal);

  const dotColor = hasLinkedPortal ? sharedFieldDotColor : undefined;
  const dotTooltipText = hasLinkedPortal
    ? 'This field is shared between your EIS and LP systems. Changes here will affect both systems.'
    : undefined;

  return (
    <Column fullWidth spacing="lg">
      {hasLinkedPortal ? (
        <Typography variant="body2" color="grey.500">
          {sharedFieldWarningText}
        </Typography>
      ) : null}

      <FormField
        label="Portal subdomain"
        size="md"
        dotColor={dotColor}
        dotTooltipText={dotTooltipText}
      >
        <Controller
          name="portalSubdomain"
          control={control}
          render={({ field, fieldState: { error } }) => (
            <TextField
              {...field}
              variant="outlined"
              size="small"
              fullWidth
              placeholder="Enter portal subdomain"
              error={!!error}
              helperText={error?.message}
              InputProps={{
                endAdornment: (
                  <InputAdornment position="end">
                    <Typography variant="body2" color="grey.600">
                      .joinfurther.com
                    </Typography>
                  </InputAdornment>
                ),
              }}
            />
          )}
        />
      </FormField>

      <Divider />

      <FormField
        label="Light main logo"
        size="md"
        spacing="md"
        dotColor={dotColor}
        dotTooltipText={dotTooltipText}
      >
        <Column spacing="md">
          <Typography variant="body2" color="grey.600">
            Used on darker backgrounds. Upload a transparent version of your
            logo that appears clearly on darker tones for the best contrast and
            visibility.
          </Typography>
          <LogoUploadBox
            name="lightMainLogo"
            boxVariant="dark"
            control={control}
          />
        </Column>
      </FormField>

      <Divider />

      <FormField
        label="Dark main logo"
        size="md"
        spacing="md"
        dotColor={dotColor}
        dotTooltipText={dotTooltipText}
      >
        <Column spacing="md">
          <Typography variant="body2" color="grey.600">
            Used on lighter backgrounds. Upload a transparent version of your
            logo that appears clearly on lighter tones for the best contrast and
            visibility.
          </Typography>
          <LogoUploadBox
            name="darkMainLogo"
            boxVariant="light"
            control={control}
          />
        </Column>
      </FormField>

      <Divider />

      <FormField
        label="Panel color"
        size="md"
        spacing="sm"
        dotColor={dotColor}
        dotTooltipText={dotTooltipText}
      >
        <Column spacing="sm">
          <Typography variant="body2" color="grey.600">
            Used across primary surfaces such as headers, side panels, and login
            screens. We recommend choosing a darker tone so that light text and
            the divider line beneath your logo remain clearly visible.
          </Typography>
          <Controller
            name="panelColor"
            control={control}
            render={({ field: { ...rest } }) => <ColourPicker {...rest} />}
          />
        </Column>
      </FormField>

      <FormField label="Secondary colour" size="md" spacing="sm">
        <Column spacing="sm">
          <Typography variant="body2" color="grey.600">
            Used for items that sit inside the panel. Choose a lighter or more
            vibrant tone that complements your panel colour.
          </Typography>
          <Controller
            name="secondaryColor"
            control={control}
            render={({ field: { ...rest } }) => <ColourPicker {...rest} />}
          />
        </Column>
      </FormField>

      <FormField label="Highlight colour" size="md" spacing="sm">
        <Column spacing="sm">
          <Typography variant="body2" color="grey.600">
            Used on white backgrounds for elements such as charts, tabs, and
            links. Choose a vibrant tone that stays readable and accessible.
          </Typography>
          <Controller
            name="highlightColor"
            control={control}
            render={({ field: { ...rest } }) => <ColourPicker {...rest} />}
          />
        </Column>
      </FormField>

      <Divider />

      <FormField
        label="Square logo"
        size="md"
        spacing="sm"
        dotColor={dotColor}
        dotTooltipText={dotTooltipText}
      >
        <Column spacing="md">
          <Typography variant="body2" color="grey.600">
            Appears in the browser tab for your investor portal (the favicon
            logo) and across the admin site (as your firm's logo - only visible
            to you). Upload a clear image (minimum 100 x 100 px) with equal
            height and width for best results.
          </Typography>
          <LogoUploadBox
            name="squareLogo"
            boxVariant="light"
            isCompact
            control={control}
          />
        </Column>
      </FormField>

      <Divider />

      <Column spacing="md">
        <Row centered spacing="md" justify="space-between">
          <Typography variant="h6">Legal footer text</Typography>
          <Toggle
            options={[
              { label: 'Portal', value: LegalFooterTextTab.Portal },
              { label: 'Email', value: LegalFooterTextTab.Email },
            ]}
            value={legalFooterTextTab}
            onChange={setLegalFooterTextTab}
          />
        </Row>
        <Controller
          name="portalFooterText"
          control={control}
          render={({ field: { ...rest }, formState: { isSubmitting } }) => (
            <CkEditor
              {...rest}
              placeholder="Enter portal footer text"
              style={{
                display:
                  legalFooterTextTab === LegalFooterTextTab.Portal
                    ? 'block'
                    : 'none',
                maxHeight: 400,
                overflowY: 'auto',
              }}
              disabled={isSubmitting}
            />
          )}
        />
        <Controller
          name="emailFooterText"
          control={control}
          render={({ field: { ...rest }, formState: { isSubmitting } }) => (
            <CkEditor
              {...rest}
              placeholder="Enter email footer text"
              style={{
                display: legalFooterTextTab === 'email' ? 'block' : 'none',
                maxHeight: 400,
                overflowY: 'auto',
              }}
              disabled={isSubmitting}
            />
          )}
        />
      </Column>
    </Column>
  );
};

export default PortalBranding;
